import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  MessageCircle, 
  Heart, 
  Shield, 
  Ticket,
  Trophy,
  Users,
  Clock,
  ArrowRight,
  Phone,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';

const LOGO_URL = "/images/logo-tuprima.png";
const WHATSAPP_SUPPORT = "573206313258";

export default function HomePage() {
  const { isAuthenticated } = useAuth();
  const [raffle, setRaffle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const activeRaffle = await api.getActiveRaffle();
        setRaffle(activeRaffle);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const openWhatsAppRefund = () => {
    const message = encodeURIComponent(
      `Hola TuPrima 👋\n\n` +
      `Solicito información sobre la devolución de mi compra.\n\n` +
      `Adjunto:\n` +
      `- Mi número de ticket(s)\n` +
      `- Comprobante de pago\n\n` +
      `Gracias.`
    );
    window.open(`https://wa.me/${WHATSAPP_SUPPORT}?text=${message}`, '_blank');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-100 to-white flex items-center justify-center">
        <div className="animate-pulse">
          <img src={LOGO_URL} alt="TuPrima" className="h-16 opacity-50" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero - Cierre Temporal */}
      <div className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 text-white">
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          {/* Logo */}
          <img src={LOGO_URL} alt="TuPrima" className="h-20 mx-auto mb-6" />
          
          {/* Mensaje Principal */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 mb-8">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-400" />
            <h1 className="text-2xl md:text-3xl font-bold mb-4">
              Cerramos puertas temporalmente
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed mb-6">
              Lamentamos informarte que después de 152 días, la dinámica no fue adoptada 
              como esperábamos y no logramos completar la venta del primer sorteo.
            </p>
            <p className="text-slate-400 text-base">
              Agradecemos a quienes confiaron en nosotros. Si realizaste una compra 
              y deseas solicitar tu devolución, por favor comunícate con nosotros.
            </p>
          </div>

          {/* WhatsApp Grande y Claro */}
          <div className="bg-emerald-600 rounded-2xl p-8 mb-8">
            <Phone className="w-10 h-10 mx-auto mb-3 text-emerald-200" />
            <p className="text-emerald-200 text-sm mb-2">Línea de atención para devoluciones:</p>
            <p className="text-4xl md:text-5xl font-bold font-mono tracking-wider mb-4">
              320 631 3258
            </p>
            <Button 
              onClick={openWhatsAppRefund}
              size="lg"
              className="bg-white hover:bg-emerald-50 text-emerald-700 rounded-full px-8 h-14 text-lg font-semibold"
              data-testid="whatsapp-refund-btn"
            >
              <MessageCircle className="w-6 h-6 mr-3" />
              Solicitar Devolución por WhatsApp
            </Button>
            <p className="text-emerald-200 text-xs mt-4">
              Adjunta tu comprobante de pago y número(s) de ticket asignados
            </p>
          </div>

          {/* Mensaje de Esperanza */}
          <div className="text-center">
            <Heart className="w-8 h-8 mx-auto mb-3 text-pink-400" />
            <p className="text-slate-300 text-base leading-relaxed max-w-xl mx-auto">
              Estamos reuniendo fuerzas y recursos para volver con más fuerza. 
              Regresaremos con <span className="text-white font-semibold">licencias oficiales</span> y 
              una <span className="text-white font-semibold">estrategia de marketing más sólida</span>.
            </p>
            <p className="text-slate-400 text-sm mt-4">
              ¡Gracias por ser parte de esta primera etapa!
            </p>
          </div>
        </div>
      </div>

      {/* Información de Transparencia */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <Shield className="w-10 h-10 mx-auto mb-3 text-slate-600" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Registro de Transparencia</h2>
          <p className="text-slate-600 text-sm">
            Información verificable del sorteo STP0001
          </p>
        </div>

        {/* Stats del Sorteo */}
        {raffle && (
          <Card className="mb-6 border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-800">{raffle.name}</h3>
                <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                  Suspendido
                </span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="text-center p-3 bg-slate-50 rounded-xl">
                  <Ticket className="w-5 h-5 mx-auto mb-1 text-blue-500" />
                  <p className="text-xl font-bold text-slate-800">{raffle.sold_tickets}</p>
                  <p className="text-xs text-slate-500">Tickets Vendidos</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-xl">
                  <Users className="w-5 h-5 mx-auto mb-1 text-emerald-500" />
                  <p className="text-xl font-bold text-slate-800">{raffle.total_tickets}</p>
                  <p className="text-xs text-slate-500">Total Disponibles</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-xl">
                  <Trophy className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                  <p className="text-xl font-bold text-slate-800">{formatCurrency(raffle.prize_amount)}</p>
                  <p className="text-xs text-slate-500">Premio Mayor</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-xl">
                  <Clock className="w-5 h-5 mx-auto mb-1 text-purple-500" />
                  <p className="text-xl font-bold text-slate-800">{raffle.progress_percentage?.toFixed(1)}%</p>
                  <p className="text-xs text-slate-500">Progreso</p>
                </div>
              </div>

              {/* Barra de Progreso */}
              <div className="w-full bg-slate-200 rounded-full h-3 mb-2">
                <div 
                  className="bg-gradient-to-r from-amber-400 to-amber-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${raffle.progress_percentage || 0}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 text-center">
                {raffle.sold_tickets} de {raffle.total_tickets} tickets vendidos
              </p>
            </CardContent>
          </Card>
        )}

        {/* Premios que se habrían entregado */}
        <Card className="mb-6 border-slate-200">
          <CardContent className="p-6">
            <h3 className="font-bold text-slate-800 mb-4">Estructura de Premios (No Ejecutado)</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  <span className="text-slate-700">Premio Mayor</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-800">$1.000.000</span>
                  <XCircle className="w-4 h-4 text-slate-400" />
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Trophy className="w-5 h-5 text-purple-500" />
                  <span className="text-slate-700">Premio Invertido</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-800">$200.000</span>
                  <XCircle className="w-4 h-4 text-slate-400" />
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Trophy className="w-5 h-5 text-emerald-500" />
                  <span className="text-slate-700">Top Comprador</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-800">$100.000</span>
                  <XCircle className="w-4 h-4 text-slate-400" />
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-4 text-center">
              El sorteo no se ejecutó. No se entregaron premios.
            </p>
          </CardContent>
        </Card>

        {/* CTA de Reclamo */}
        <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
          <CardContent className="p-6 text-center">
            <CheckCircle className="w-10 h-10 mx-auto mb-3 text-emerald-500" />
            <h3 className="font-bold text-emerald-900 mb-2">¿Compraste tickets?</h3>
            <p className="text-emerald-700 text-sm mb-4">
              Si tienes comprobante de pago y tickets asignados, 
              puedes solicitar tu devolución.
            </p>
            <Button 
              onClick={openWhatsAppRefund}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6"
              data-testid="claim-refund-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Reclama Aquí
            </Button>
          </CardContent>
        </Card>

        {/* Links de Información */}
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link to="/transparencia" className="text-sm text-slate-500 hover:text-emerald-600">
            Transparencia
          </Link>
          <span className="text-slate-300">•</span>
          <Link to="/terms" className="text-sm text-slate-500 hover:text-emerald-600">
            Términos y Condiciones
          </Link>
          <span className="text-slate-300">•</span>
          <Link to="/como-participar" className="text-sm text-slate-500 hover:text-emerald-600">
            ¿Cómo funcionaba?
          </Link>
        </div>

        {/* Mensaje Final */}
        <div className="mt-12 text-center">
          <p className="text-slate-400 text-sm">
            TuPrima - La que alegra el día
          </p>
          <p className="text-slate-300 text-xs mt-1">
            Volveremos más fuertes 💪
          </p>
        </div>
      </div>

      {/* Floating WhatsApp Button */}
      <button
        onClick={openWhatsAppRefund}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-emerald-500 hover:bg-emerald-600 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
        data-testid="floating-whatsapp"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </button>
    </div>
  );
}
